import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Copy, Trash2, Layout, Sparkles, Loader2, Plus } from 'lucide-react';

const LAYOUTS = ['centered', 'left', 'right', 'grid'];
const SECTION_COLOR_FIELDS = [
  { key: 'backgroundColor', label: 'Background' },
  { key: 'textColor', label: 'Text' },
  { key: 'accentColor', label: 'Accent' },
];

export const SectionEditor = ({
  section,
  updateSection,
  duplicateSection,
  deleteSection,
  regeneratePrompt,
  setRegeneratePrompt,
  regenerateSection,
  isRegenerating,
}) => {
  if (!section) {
    return (
      <div className="flex-1 overflow-y-auto p-6">
        <div className="h-full flex items-center justify-center text-center">
          <div>
            <Layout className="w-16 h-16 text-[#1e3a5f] mx-auto mb-4" />
            <h3 className="text-[#7dd3fc] font-bold text-xl mb-2">Select a Section</h3>
            <p className="text-[#888680]">Click on a section in the sidebar to edit it</p>
          </div>
        </div>
      </div>
    );
  }

  const setItems = (items) => updateSection(section.id, { items });

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-3xl mx-auto">
        <div className="bg-[#302f2c] border border-[#1e3a5f]/50 rounded-lg overflow-hidden">
          {/* Section Header */}
          <div className="p-4 border-b border-[#1e3a5f]/50 flex items-center justify-between">
            <div>
              <h2 className="text-[#7dd3fc] font-bold text-lg">Edit Section</h2>
              <span className="text-[#888680] text-sm capitalize">{section.type}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => duplicateSection(section)}
                variant="outline"
                data-testid="duplicate-section-btn"
                className="border-[#1e3a5f] text-[#888680] hover:text-[#7dd3fc] p-2"
              >
                <Copy size={16} />
              </Button>
              <Button
                onClick={() => deleteSection(section.id)}
                variant="outline"
                data-testid="delete-section-btn"
                className="border-[#1e3a5f] text-[#888680] hover:text-red-500 p-2"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div>
              <label className="text-[#7dd3fc] text-sm font-semibold uppercase tracking-wider mb-2 block">Title</label>
              <Input
                value={section.title || ''}
                onChange={(e) => updateSection(section.id, { title: e.target.value })}
                className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] h-12"
              />
            </div>

            <div>
              <label className="text-[#7dd3fc] text-sm font-semibold uppercase tracking-wider mb-2 block">Subtitle</label>
              <Input
                value={section.subtitle || ''}
                onChange={(e) => updateSection(section.id, { subtitle: e.target.value })}
                className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] h-12"
              />
            </div>

            <div>
              <label className="text-[#7dd3fc] text-sm font-semibold uppercase tracking-wider mb-2 block">Content</label>
              <Textarea
                value={section.content || ''}
                onChange={(e) => updateSection(section.id, { content: e.target.value })}
                rows={4}
                className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] resize-none"
              />
            </div>

            <div>
              <label className="text-[#7dd3fc] text-sm font-semibold uppercase tracking-wider mb-2 block">Layout</label>
              <div className="grid grid-cols-4 gap-2">
                {LAYOUTS.map((layout) => (
                  <button
                    key={layout}
                    onClick={() => updateSection(section.id, { layout })}
                    className={`px-3 py-2 rounded-lg border text-sm capitalize transition-all ${
                      section.layout === layout
                        ? 'bg-[#7dd3fc] text-[#1a1c1b] border-[#7dd3fc]'
                        : 'bg-[#1a1c1b] text-[#888680] border-[#1e3a5f] hover:border-[#7dd3fc]/50'
                    }`}
                  >
                    {layout}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[#7dd3fc] text-sm font-semibold uppercase tracking-wider mb-2 block">Section Colors</label>
              <div className="grid grid-cols-3 gap-4">
                {SECTION_COLOR_FIELDS.map(({ key, label }) => (
                  <div key={key}>
                    <span className="text-[#888680] text-xs block mb-1">{label}</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={section.styles?.[key] || '#ffffff'}
                        onChange={(e) => updateSection(section.id, {
                          styles: { ...section.styles, [key]: e.target.value }
                        })}
                        className="w-8 h-8 rounded cursor-pointer bg-transparent border border-[#1e3a5f]"
                      />
                      <span className="text-[#888680] text-xs">{section.styles?.[key]}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Items Editor */}
            {section.items && section.items.length > 0 && (
              <div>
                <label className="text-[#7dd3fc] text-sm font-semibold uppercase tracking-wider mb-2 block">
                  Items ({section.items.length})
                </label>
                <div className="space-y-3">
                  {section.items.map((item, idx) => (
                    <div key={`${section.id}-item-${idx}`} className="bg-[#1a1c1b] p-4 rounded-lg border border-[#1e3a5f]/50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[#888680] text-xs">Item {idx + 1}</span>
                        <button
                          onClick={() => setItems(section.items.filter((_, i) => i !== idx))}
                          className="text-[#888680] hover:text-red-500"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                      <Input
                        value={item.title || item.name || ''}
                        onChange={(e) => {
                          const newItems = [...section.items];
                          newItems[idx] = { ...newItems[idx], title: e.target.value, name: e.target.value };
                          setItems(newItems);
                        }}
                        placeholder="Title"
                        className="bg-[#302f2c] border-[#1e3a5f] text-[#7dd3fc] text-sm h-9 mb-2"
                      />
                      <Textarea
                        value={item.description || item.content || ''}
                        onChange={(e) => {
                          const newItems = [...section.items];
                          newItems[idx] = { ...newItems[idx], description: e.target.value, content: e.target.value };
                          setItems(newItems);
                        }}
                        placeholder="Description"
                        rows={2}
                        className="bg-[#302f2c] border-[#1e3a5f] text-[#7dd3fc] text-sm resize-none"
                      />
                    </div>
                  ))}
                </div>
                <Button
                  onClick={() => setItems([...section.items, { title: 'New Item', description: 'Description' }])}
                  variant="outline"
                  className="w-full mt-3 border-dashed border-[#1e3a5f] text-[#888680] hover:text-[#7dd3fc]"
                >
                  <Plus size={14} className="mr-1" /> Add Item
                </Button>
              </div>
            )}

            {/* AI Regenerate */}
            <div className="bg-[#1a1c1b] p-4 rounded-lg border border-[#1e3a5f]/50">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[#7dd3fc]" />
                <span className="text-[#7dd3fc] font-semibold text-sm">AI Regenerate</span>
              </div>
              <Textarea
                value={regeneratePrompt}
                onChange={(e) => setRegeneratePrompt(e.target.value)}
                placeholder="Describe changes you want AI to make to this section..."
                rows={2}
                className="bg-[#302f2c] border-[#1e3a5f] text-[#7dd3fc] text-sm mb-3 resize-none"
              />
              <Button
                onClick={() => regenerateSection(section.id)}
                disabled={isRegenerating || !regeneratePrompt}
                className="w-full bg-[#1e3a5f] text-[#7dd3fc] hover:bg-[#7dd3fc] hover:text-[#1a1c1b] rounded-lg"
              >
                {isRegenerating ? (
                  <><Loader2 size={14} className="mr-2 animate-spin" /> Regenerating...</>
                ) : (
                  <><Sparkles size={14} className="mr-2" /> Regenerate with AI</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
